<template>
  <div :ref="playlistId" class="mc-trackvideo-card__item mc-trackvideo-card__playlist">
    <div class="mc-trackvideo-card__item-wrapper">
      <div
        class="mc-trackvideo-card-header-bg-1"
        :style="`background-color: ${listColor[uniqueVehicleIds.indexOf(playlistItem.vehicleId)]}`"
      >
        <span class="mc-trackvideo-card-header-label" :title="headerTitle">
          {{ headerTitle }}
        </span>
        <div
          :class="`mc-trackvideo-card-header-status bg-${motionStatusColor(
            calculatedMotionStatus
          )}`"
        >
          {{ motionStatusFormat(calculatedMotionStatus) }}
        </div>
      </div>
      <div class="mc-trackvideo-card-body">
        <div class="mc-trackvideo-card-body-content">
          <Sleep
            v-if="playlistItem.videoStatus === 'sleep'"
            :imei="playlistItem.imei"
            @countdownEnd="countdownSleepEnd"
            @sendCommandStream="sendCommandStream"
          />
          <VideoTimeout
            v-if="playlistItem.videoStatus === 'timeout'"
            @clickContinue="clickContinueStream(index, playlistItem)"
          />
          <Connecting v-if="playlistItem.videoStatus === 'connecting'" />
          <NoFeedBack
            v-if="playlistItem.videoStatus === 'error'"
            @clickTryAgain="onClickTryAgain"
          />
          <MeVideo
            v-show="playlistItem.videoStatus === 'ready' && !playlistItem.refresh"
            v-if="!isRemoved && playlistItem.videoStatus !== 'timeout'"
            :ref="`video-item-${playlistId}`"
            :source="playlistItem"
            :playlistId="playlistId"
            :isMdvrSleep="isMdvrSleep"
            @startPlay="onStartPlay"
            @ready="onPlayerReady"
            @error="onPlayerError"
            @pause="pauseVideo"
            @mute="muteVideo"
            @fullscreen="onFullscreen"
            @setPlaylistReady="setPlaylistVideoStatusReady"
            @setPlaylistTimeout="setPlaylistVideoStatusTimeout"
            @connecting="connecting"
            @sleep="onPlayerSleep"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import NoFeedBack from './State/NoFeedBack'
import Connecting from './State/Connecting'
import Sleep from './State/Sleep.vue'
import VideoTimeout from './State/Timeout.vue'
import MeVideo from './State/Video'
import { MixPanel } from '@/mixin/mixpanel'
import { Helper } from '@/mixin/helper'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'VideoItem',
  components: {
    NoFeedBack,
    Connecting,
    Sleep,
    VideoTimeout,
    MeVideo,
  },
  mixins: [Helper, MixPanel],
  props: {
    playlistId: {
      type: String,
      required: true,
    },
    playlist: {
      type: Object,
      required: true,
    },
    uniqueVehicleIds: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      playlistItem: this.playlist,
      listColor: [
        '#3D69A7',
        '#082957',
        '#A56DE5',
        '#562C85',
        '#D64AA8',
        '#7A1158',
        '#478EE1',
        '#114683',
        '#06172C',
        '#083222',
        '#663700',
        '#620C00',
        '#344264',
        '#01133D',
        '#310600',
        '#331C00',
      ],
    }
  },
  computed: {
    ...mapGetters('video', ['vehicleStatusByVehicleId']),
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    calculatedMotionStatus() {
      return this.getVehicleStatusByVehicleId(this.playlistItem.vehicleId).calculatedMotionStatus
    },
    isMdvrSleep() {
      return (
        this.isDeviceBsjMdvr(this.playlistItem.deviceCode) && this.calculatedMotionStatus === 'P'
      )
    },
    isRemoved() {
      return this.playlist.isRemoved
    },
    headerTitle() {
      return (
        this.playlistItem.licensePlate +
        ' - ' +
        `${this.$t('trackVideo.camera')} ${this.playlistItem.camera}`
      )
    },
    isVssHowen() {
      return this.playlistItem.deviceType === 'vss-howen'
    },
  },
  async mounted() {
    if (this.isMdvrSleep) {
      this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'sleep')
    } else {
      this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'connecting')
    }
  },
  methods: {
    setPlaylistValue(playlistId, key, value) {
      this.$emit('setPlaylistValue', playlistId, key, value)
    },
    connecting() {
      console.log('video state is connecting')
      this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'connecting')
    },

    setPlaylistVideoStatusTimeout() {
      setTimeout(() => {
        console.log('video state is is timeout')
        this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'timeout')
        this.$forceUpdate()
      }, 300)
    },

    setPlaylistVideoStatusReady() {
      setTimeout(() => {
        console.log('video state is ready')
        this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'ready')
        this.$forceUpdate()
      }, 300)
    },

    async onStartPlay() {
      const data = this.playlistItem
      const isJc = data.deviceType === 'jc'
      const imei = data.imei
      const channel = isJc ? (parseInt(data.channel) + 1).toString() : data.channel
      await this.$store.dispatch('video/requestOnlineCommandStreamStatus', {
        channels: [channel],
        imei,
        status: true,
        statsDataV2: {
          inputDataPlay: {
            channel: channel,
            id: data.uid,
          },
        },
      })
    },

    onPlayerReady() {
      setTimeout(() => {
        console.log('video state is ready')
        const data = this.playlistItem
        this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'ready')
        this.$forceUpdate()
        const videoItemRefs = `video-item-${this.playlistId}`
        this.$refs[videoItemRefs].$refs[`videoPlayer${data.id}`].player.muted = true
      }, 300)
    },

    // for mdvr only when sleep
    onPlayerSleep() {
      if (!this.isMdvrSleep) return
      console.log('video state is sleep')
      this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'sleep')
      this.$forceUpdate()
    },

    countdownSleepEnd() {
      const videoItemRefs = `video-item-${this.playlistId}`
      const videoRef = this.$refs[videoItemRefs]

      if (videoRef && videoRef.countdownSleepEnd) {
        videoRef.countdownSleepEnd()
      }

      this.onPlayerError()
    },

    onPlayerError() {
      setTimeout(async () => {
        console.log('video state is error')
        const data = this.playlistItem
        this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'error')
        this.$forceUpdate()
        const imei = data.imei

        const isJc = data.deviceType === 'jc'
        const channel = isJc ? (parseInt(data.channel) + 1).toString() : data.channel

        await this.$store.dispatch('video/requestOnlineCommandStreamStatus', {
          channels: [channel],
          imei,
          status: true,

          statsDataV2: {
            inputDataTryAgain: {
              channel,
              id: data.uid,
            },
          },
        })
      }, 300)
    },

    pauseVideo() {
      const data = this.playlistItem
      this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'pause')
      this.$forceUpdate()

      const videoItemRefs = `video-item-${this.playlistId}`
      this.$refs[videoItemRefs].$refs[`videoPlayer${data.id}`].player.pause()
      this.$refs[videoItemRefs].$refs[`videoPlayer${data.id}`].player.muted = true
    },

    muteVideo() {
      const data = this.playlistItem
      this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'ready')
      this.$forceUpdate()

      const videoItemRefs = `video-item-${this.playlistId}`

      this.$refs[videoItemRefs].$refs[`videoPlayer${data.id}`].player.muted =
        !this.$refs[videoItemRefs].$refs[`videoPlayer${data.id}`].player.muted
    },

    async onFullscreen() {
      this.setPlaylistValue(`${this.playlistId}`, 'fullscreen', !this.playlistItem.fullscreen)

      await this.$nextTick()

      this.$forceUpdate()

      if (this.playlistItem.fullscreen) {
        var elem = document.documentElement

        if (elem.requestFullscreen) {
          elem.requestFullscreen()
        } else if (elem.webkitRequestFullscreen) {
          /* Safari */
          elem.webkitRequestFullscreen()
        } else if (elem.msRequestFullscreen) {
          /* IE11 */
          elem.msRequestFullscreen()
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          /* Safari */
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          /* IE11 */
          document.msExitFullscreen()
        }
      }
    },

    clickContinueStream() {
      this.track('Continue streaming the video')
      if (this.isVssHowen) {
        this.startNewSessionStreamVss()
      } else {
        this.startNewSessionStream()
      }
    },

    async onClickTryAgain() {
      if (this.isVssHowen) {
        this.startNewSessionStreamVss()
        return
      }
      await this.startNewSessionStream()
      this.$forceUpdate()

      this.reconnecting()
    },

    reconnecting() {
      this.setPlaylistValue(`${this.playlistId}`, 'refresh', true)

      setTimeout(() => {
        if (this.isMdvrSleep) {
          this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'sleep')
        } else {
          this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'connecting')
        }

        this.$forceUpdate()

        const videoItemRefs = `video-item-${this.playlistId}`

        this.$refs[videoItemRefs].$refs[`videoPlayer${this.playlistId}`].initialize()

        this.setPlaylistValue(`${this.playlistId}`, 'refresh', false)
      }, 300)
    },
    async startNewSessionStreamVss() {
      this.setPlaylistValue(`${this.playlistId}`, 'videoStatus', 'connecting')

      this.$forceUpdate()
    },

    async startNewSessionStream() {
      const data = this.playlistItem
      const imei = data.imei

      const isJc = data.deviceType === 'jc'
      const channel = isJc ? (parseInt(data.channel) + 1).toString() : data.channel
      const uid = uuidv4()
      if (!data.usingWsFlv) {
        const response = await this.$store.dispatch('video/requestOnlineCommandStreamStatus', {
          imei,
          channels: [channel],
          status: true,
          streamingEndpoint: data.streamingEndpoint,
          statsDataV2: {
            inputDataRequest: [
              {
                channel,
                id: uid,
              },
            ],
          },
        })
        const channelNotSent =
          response.data.setVideoStreamStatus.setVideoStreamStatus.channelNotSent

        if (Number(channelNotSent?.[0]) === Number(channel)) {
          return this.$mcToast.error(this.$t('videoHistory.video.playbackOrStreamingGoinOn'))
        }
      }
      if (this.isMdvrSleep) {
        this.setPlaylistValue(`${data.id}`, 'videoStatus', 'sleep')
      } else {
        this.setPlaylistValue(`${data.id}`, 'videoStatus', 'connecting')
      }
      this.setPlaylistValue(`${data.id}`, 'uid', uid)
      this.$forceUpdate()
    },

    checkIsDemo(vehicleId) {
      const dataDemoTrackvision = this.getFeatureFlagValue('vsms2_trackvision2_stream_demo_id')
      return Object.keys(dataDemoTrackvision)
        .map((id) => parseInt(id))
        .includes(vehicleId)
    },
    getVehicleStatusByVehicleId(vehicleId) {
      if (this.checkIsDemo(vehicleId)) {
        return {
          ...this.vehicleStatusByVehicleId(vehicleId),
          calculatedMotionStatus: 'M',
        }
      }
      return this.vehicleStatusByVehicleId(vehicleId)
    },
    sendCommandStream() {
      const imei = this.playlistItem.imei,
        channel = this.playlistItem.channel,
        uid = this.playlistItem.uid,
        streamingEndpoint = this.playlistItem.streamingEndpoint

      this.$store.dispatch('video/requestOnlineCommandStreamStatus', {
        imei,
        channels: [channel],
        status: true,
        streamingEndpoint: streamingEndpoint,
        statsDataV2: {
          inputDataRequest: [
            {
              channel,
              id: uid,
            },
          ],
        },
      })
    },
  },
}
</script>
